import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import reportHealthService from "../../../../../../../../../Service/OPSServices/StoreHealthServices/reportHealthService.js";
import SavedMatrix from "./SavedMatrix";
import SavedPanel from "./SavedPanel";
import { size } from "./PayloadView";

const PAGE_SIZE = 50;
const LEVEL = { 2: "red", 1: "amber", 0: "ok" };
const FILTERS = [["all", "All"], ["red", "Missing data"], ["amber", "Pending"], ["ok", "All saved"]];
const fmt = (v) => new Intl.NumberFormat("en-US").format(v || 0);

function Pager({ page, pages, onPage }) {
  return (
    <div className="rh-pager" role="navigation" aria-label="Stores pages">
      <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
      <span>Page {page} of {pages}</span>
      <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} aria-label="Next page">›</button>
    </div>
  );
}

/** Saved data view: is at least one payload row saved for each store's usual reports, for the report date. */
export default function SavedView({ date, onDate, reloadToken, onLoading, onRetry, retrying }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const latest = useRef(0);
  const first = useRef(true);

  const load = useCallback(async (fresh) => {
    const call = ++latest.current;
    onLoading(true);
    try {
      const d = await reportHealthService.getSaved(date, fresh);
      if (call === latest.current) { setData(d); setError(null); }
    } catch (e) {
      if (call === latest.current) setError(e.message);
    } finally {
      if (call === latest.current) onLoading(false);
    }
  }, [date, onLoading]);
  useEffect(() => { load(!first.current); first.current = false; }, [load, reloadToken]);

  const counts = useMemo(() => {
    const c = { all: 0, red: 0, amber: 0, ok: 0 };
    (data ? data.stores : []).forEach((s) => { c.all += 1; c[LEVEL[s.severity]] += 1; });
    return c;
  }, [data]);
  const stores = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.stores.filter((s) => filter === "all" || LEVEL[s.severity] === filter)
      .filter((s) => !needle || [s.storeName, s.profileId, s.market].join(" ").toLowerCase().includes(needle));
  }, [data, filter, q]);
  useEffect(() => { setPage(1); }, [filter, q, date]);
  const pages = Math.max(1, Math.ceil(stores.length / PAGE_SIZE));
  const pageNo = Math.min(page, pages);
  const columns = useMemo(() => (data ? data.columns.filter((c) => data.stores.some((s) => s.cells[c.name] && s.cells[c.name].state !== "none")) : []), [data]);

  if (error) return <div className="ams-banner ams-banner--err" role="status">Could not load saved data: {error}.</div>;
  if (!data) return <div className="ams-empty">Loading saved data…</div>;
  if (data.notice) {
    return (
      <div className="ams-banner" role="status">
        <span>{data.notice}
          {data.suggestedDate && onDate && (
            <button type="button" className="ams-link" onClick={() => onDate(data.suggestedDate)}>Show {data.suggestedDate}</button>
          )}
        </span>
      </div>
    );
  }
  const s = data.summary;
  const cards = [
    { k: s.managedList ? "Managed stores" : "Stores", v: s.stores, sub: `${fmt(s.storesRed)} missing data${s.storesNoReports ? `, ${fmt(s.storesNoReports)} with none` : ""}`, c: s.storesRed ? "red" : "" },
    { k: "Reports with data", v: s.saved, sub: `of ${fmt(s.expected)} expected`, c: s.saved < s.expected ? "" : "ok" },
    { k: "Missing", v: s.missing, sub: s.retryable ? `${fmt(s.retryable)} can be retried` : s.notYet ? `${fmt(s.notYet)} more not saved yet today` : "nothing saved, see why in each cell", c: s.missing ? "red" : "ok" },
    { k: "Pending", v: s.pending, sub: "requested, saved by the next run", c: s.pending ? "amber" : "" },
    { k: "0-row reports", v: s.empty, sub: "Amazon returned no data (no ad activity)", c: "" },
    s.countsRecorded && !s.uncounted
      ? { k: "Data rows", v: s.dataRows, sub: `cost ${Number(s.cost || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })} · ${size(s.bytes)}`, c: "" }
      : { k: "Payload rows", v: s.payloadRows, sub: `${size(s.bytes)} saved for this date`, c: "" },
  ];
  return (
    <>
      <div className="ams-cards">
        {cards.map((c) => (
          <div className="ams-card" key={c.k}>
            <div className="ams-card-k">{c.k}</div>
            <div className={`ams-card-v${c.c ? ` ams-card-v--${c.c}` : ""}`}>{c.text || fmt(c.v)}</div>
            <div className="ams-card-s">{c.sub}</div>
          </div>
        ))}
      </div>
      <div className="ams-toolbar">
        <div className="ams-chips" role="group" aria-label="Filter">
          {FILTERS.map(([key, label]) => (
            <button key={key} type="button" className={`ams-chip ams-chip--${key}`} aria-pressed={filter === key} onClick={() => setFilter(key)}>
              {label}<span className="ams-chip-count">{counts[key]}</span>
            </button>
          ))}
        </div>
        <div className="rh-toolbar-right">
          <Pager page={pageNo} pages={pages} onPage={setPage} />
          <input className="ams-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search store, profile or market" aria-label="Search stores" />
        </div>
      </div>
      <div className="rh-legend">
        <span className="rh-key"><i className="rh-dot rh-dot--ok" />Saved: data rows (or size, for reports saved before counts were recorded)</span>
        <span className="rh-key"><i className="rh-dot rh-dot--amber" />Pending: requested, saved by the next run</span>
        <span className="rh-key"><i className="rh-dot rh-dot--none" />0 rows: Amazon returned no data</span>
        <span className="rh-key"><i className="rh-dot rh-dot--red" />Missing: nothing saved; the reason from the request log is under it</span>
        <span className="rh-key"><i className="rh-dot rh-dot--none" />Not saved yet today</span>
        <span className="rh-key">– not used by the store</span>
        <span className="rh-key rh-key--tip">Click a cell for its saved data, or for what the request log says and Retry.</span>
      </div>
      <SavedMatrix columns={columns} stores={stores.slice((pageNo - 1) * PAGE_SIZE, pageNo * PAGE_SIZE)} retrying={retrying}
                   onOpen={(store, column) => setOpen({ store, column })} />
      {pages > 1 && <div className="rh-pager-bottom"><Pager page={pageNo} pages={pages} onPage={setPage} /></div>}
      {open && <SavedPanel date={date} store={open.store} column={open.column} cell={open.store.cells[open.column.name]}
                           onClose={() => setOpen(null)} onRetry={onRetry}
                           retrying={retrying && retrying.has(`${open.store.profileId}|${open.column.name}`)} />}
    </>
  );
}
